/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 24th 2023, 3:43:30 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Typography from "@mui/material/Typography";
import { getDefaultDesignLayout } from "lib/factories";
import { DesignLayout, ProcessDefinition } from "lib/schemas";
import { DesignLayoutEditor } from "service-builder/components/layouts/DesignLayoutEditor";
import { OSActionButton, EmptyStateActionWrapper, Image, EmphasisText } from "ui-widgets";

interface InstanceDetailLayoutProps {
  onSaveDetailLayout: (layout: DesignLayout) => void;
  contextTagsToAppend?: string;
  processDefinition: ProcessDefinition;
}

export const InstanceDetailLayout = ({
  onSaveDetailLayout,
  contextTagsToAppend,
  processDefinition,
}: InstanceDetailLayoutProps) => {
  const { layoutId, hasCustomLayout, newDesignLayoutDefault, autoSaveNewLayout, onClickAddCustomLayout } =
    useInstanceDetailLayout(processDefinition);

  return (
    <>
      {hasCustomLayout ? (
        <DesignLayoutEditor
          layoutId={layoutId}
          newDesignLayoutDefault={newDesignLayoutDefault}
          onSaveDesignLayout={onSaveDetailLayout}
          appendContents={contextTagsToAppend}
          autoSaveNewLayout={autoSaveNewLayout}
        />
      ) : (
        <NoCustomLayoutCTA>
          <OSActionButton variant="contained" onClick={onClickAddCustomLayout} size="large">
            Add Custom Detail Layout
          </OSActionButton>
        </NoCustomLayoutCTA>
      )}
    </>
  );
};

const useInstanceDetailLayout = (processDefinition: ProcessDefinition) => {
  const newDesignLayoutDefault = getDefaultDesignLayout({
    layout_type: "detail_display",
    organizationId: processDefinition.artifact_org,
  });

  const [autoSaveNewLayout, setAutoSaveNewLayout] = React.useState(false);

  const onClickAddCustomLayout = () => setAutoSaveNewLayout(true);

  const hasCustomLayout = Boolean(processDefinition.detail_view_layout) || autoSaveNewLayout;

  return {
    layoutId: processDefinition.detail_view_layout,
    hasCustomLayout,
    autoSaveNewLayout,
    newDesignLayoutDefault,
    onClickAddCustomLayout,
  };
};

const NoCustomLayoutCTA = ({ children }) => {
  return (
    <EmptyStateActionWrapper
      title={<>No custom detail layout</>}
      description={
        <Typography>
          You don't have a custom detail layout for this process. Custom detail layouts allow you to customize the view
          that displays information around a single instance this process. Display submission data, task review data,
          dataset values and more.{" "}
          <EmphasisText>
            Do you need more complex customizations? Get in touch with us to use our developer API.
          </EmphasisText>
        </Typography>
      }
      illustrationGraphic={<Image src="/img/layout.png" />}
    >
      {children}
    </EmptyStateActionWrapper>
  );
};
